/**
 * Order Detail Page
 *
 * Displays the details of a specific order fetched from Supabase.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Link, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
};

const getStatusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'completed': return 'success';
    case 'processing': return 'info';
    case 'pending': return 'warning';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

const OrderDetail = () => {
  const { id: orderId } = useParams();
  const { customer } = useAuth();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!customer?.id || !orderId) return;

    const fetchOrder = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('orders')
          .select('*, order_items(*)')
          .eq('id', orderId)
          .eq('customer_id', customer.id)
          .single();

        if (fetchError) throw fetchError;
        setOrder(data);
      } catch (err) {
        setError(err.message || 'Failed to fetch order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [customer?.id, orderId]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress sx={{ color: '#8B4513' }} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ my: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button component={Link} to="/account/orders" startIcon={<ArrowBackIcon />} sx={{ color: '#8B4513' }}>
          Back to Orders
        </Button>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="md" sx={{ my: 4 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>Order not found</Alert>
        <Button component={Link} to="/account/orders" startIcon={<ArrowBackIcon />} sx={{ color: '#8B4513' }}>
          Back to Orders
        </Button>
      </Container>
    );
  }

  const orderItems = order.order_items ?? [];
  const statusLabel = (order.status || 'pending').charAt(0).toUpperCase() + (order.status || 'pending').slice(1);

  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/account" style={{ textDecoration: 'none', color: '#8B4513' }}>My Account</Link>
        <Link to="/account/orders" style={{ textDecoration: 'none', color: '#8B4513' }}>Orders</Link>
        <Typography color="text.primary">Order #{order.id.slice(0, 8)}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button component={Link} to="/account/orders" startIcon={<ArrowBackIcon />} sx={{ mr: 2, color: '#8B4513' }}>
          Back
        </Button>
        <Typography variant="h4" component="h1" sx={{ color: '#8B4513' }}>
          Order Details
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Order #{order.id.slice(0, 8)}</Typography>
          <Chip label={statusLabel} color={getStatusColor(order.status)} />
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Placed on {formatDate(order.created_at)}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          {order.shipping_address && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Shipping Address</Typography>
              <Typography variant="body1">{order.shipping_address.name}</Typography>
              <Typography variant="body1">{order.shipping_address.address1}</Typography>
              {order.shipping_address.address2 && (
                <Typography variant="body1">{order.shipping_address.address2}</Typography>
              )}
              <Typography variant="body1">
                {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}
              </Typography>
              <Typography variant="body1">{order.shipping_address.country}</Typography>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>Payment Information</Typography>
            <Typography variant="body1">Payment Method: Credit Card</Typography>
            {order.payment_intent_id && (
              <Typography variant="body1">Payment ID: {order.payment_intent_id}</Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Order Items</Typography>

        {orderItems.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orderItems.map((item, index) => (
                  <TableRow key={item.id || index}>
                    <TableCell component="th" scope="row">
                      {item.product_name || item.name || 'Item'}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.unit_price ?? item.price ?? 0)}
                    </TableCell>
                    <TableCell align="right">{item.quantity ?? 1}</TableCell>
                    <TableCell align="right">
                      {formatCurrency((item.unit_price ?? item.price ?? 0) * (item.quantity ?? 1))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">No item details available.</Typography>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Card variant="outlined" sx={{ width: '300px' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Order Summary</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2">{formatCurrency(order.total ?? 0)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Shipping:</Typography>
                <Typography variant="body2">Free</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1">Total:</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(order.total ?? 0)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button component={Link} to="/account/orders" startIcon={<ArrowBackIcon />} sx={{ color: '#8B4513' }}>
          Back to Orders
        </Button>
        <Button
          component={Link}
          to="/products"
          variant="contained"
          sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6d3610' } }}
        >
          Continue Shopping
        </Button>
      </Box>
    </Container>
  );
};

export default OrderDetail;
